import { useMutation, useQueryClient } from '@mint-lib/query-client';
import { useFlags } from '@mint-lib/routing-context';
import { MediaPlan } from '@mint-sdk/api-media-plan';
import { getRouteApi } from '@mint/routing';

import { useChosenLevel } from '../../hooks/useChosenLevel.js';

const routerApi = getRouteApi(
  '/_secured/campaign/$campaignId/planning/buying-plan',
);

export const useColumnsMutationApi = () => {
  const ff = useFlags('@mint/arm', ['planningV2ColumnsPerView']);
  const perView = ff.planningV2ColumnsPerView.enabled;
  const { campaignId: planId } = routerApi.useParams();
  const { id: mediaDataId, level } = useChosenLevel();
  const queryClient = useQueryClient();

  const getColumnsQueryKey = () =>
    perView
      ? MediaPlan.getColumnsListByViewQueryOptions({
          urlParams: { planId },
          params: { view: level as MediaPlan.PlanningGridViewType },
        }).queryKey
      : MediaPlan.getColumnsListQueryOptions({
          params: { mediaDataId: mediaDataId ?? '' },
        }).queryKey;

  return useMutation({
    mutationFn: async (columns: string[]) =>
      perView
        ? MediaPlan.updateColumnsSchema({
            urlParams: { planId },
            data: { columns, view: level as MediaPlan.PlanningGridViewType },
          })
        : MediaPlan.update({
            urlParams: { planId },
            data: { columns },
          }),
    onMutate: async (columns: string[]) => {
      if (perView ? !planId : !mediaDataId) return;

      const queryKey = getColumnsQueryKey();

      await queryClient.cancelQueries({ queryKey });

      const previousData =
        queryClient.getQueryData<MediaPlan.ColumnsResponse>(queryKey);

      queryClient.setQueryData<MediaPlan.ColumnsResponse>(
        queryKey,
        (oldColumns) => {
          if (!oldColumns?.data?.items) return oldColumns;

          return {
            ...oldColumns,
            data: {
              ...oldColumns.data,
              items: oldColumns.data.items.map((col: MediaPlan.Column) => ({
                ...col,
                isHidden: !columns.includes(col.id),
              })),
            },
          };
        },
      );

      return { previousData };
    },
    onError: (_, __, context) => {
      if (!context?.previousData) {
        return;
      }

      queryClient.setQueryData<MediaPlan.ColumnsResponse>(
        getColumnsQueryKey(),
        context.previousData,
      );
    },
  });
};
